import { useStyles } from '@archipro-design/aria';
import type { ImageColumnProps } from '@modules/page-builder/types';
import DetailSingleImage from '../detail-single-image-shell/DetailSingleImageShell';
import { SingleImageContainer } from '../../common/pagebuilder.style';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { Image } from '@rocco/components/image';
import { ImageModuleExtraSpacing } from '@modules/page-builder/component/detail-mobile/detail-modules/DetailModules.style';
import LazyImageModule from '../detail-modules/LazyImageModule';

/** Number of modules to render eagerly (above-fold) */
const EAGER_COUNT = 2;

interface SingleImageModuleProps {
    column: ImageColumnProps;
    /** Index of this module for eager loading determination */
    moduleIndex?: number;
}

/**
 * Mobile-optimised single image module.
 * Uses mobile-specific image sizes (414px) for better performance.
 */
const SingleImageModule = ({
    column,
    moduleIndex = 0,
}: SingleImageModuleProps) => {
    const { css } = useStyles();
    const isEager = moduleIndex < EAGER_COUNT;

    if (!column.imageUrl) {
        return null;
    }

    return (
        <LazyImageModule
            className={css(SingleImageContainer, ImageModuleExtraSpacing)}
            dimension={column.dimension}
            moduleIndex={moduleIndex}
            eagerCount={EAGER_COUNT}
        >
            <DetailSingleImage
                caption={column.caption}
                libraryImage={column?.libraryImage}
                tags={column.tags}
                uniqueApprovedTags={column.uniqueApprovedTags}
                dimension={column.dimension}
                imageURL={column.imageUrl}
            >
                <Image
                    src={column.imageUrl}
                    width={column.dimension.width ?? undefined}
                    height={column.dimension.height ?? undefined}
                    fill
                    fit="cover"
                    loading={isEager ? 'eager' : 'lazy'}
                    fetchPriority={isEager ? 'high' : 'low'}
                    sizes={getImagesSizes({
                        desktop: column.dimension.width,
                        mobile: column.dimension.mobileWidth,
                    })}
                    alt={column.caption || column?.libraryImage?.Title || ''}
                />
            </DetailSingleImage>
        </LazyImageModule>
    );
};

export default SingleImageModule;
